import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { DEFAULT_SHOP, SHOP_META } from "../lib/shop-context";

/**
 * Strict UUID v1–v5 pattern. LegacyEnvioIdRedirect only fires for
 * shipment/order IDs, which are UUIDs. Anything that doesn't match this
 * shape is a typo, a stale link, or a literal path that slipped past the
 * literal routes above — we short-circuit to a not-found placeholder in
 * that case instead of hammering the backend with two probes.
 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Phase 33 hotfix — legacy `/envio/{id}` URL bridge.
 *
 * Pre-Phase 32, shipment confirmation emails linked to `/envio/{shipment_id}`
 * and that path was served by `EnvioConfirmacionPage`. After Phase 32 the
 * `/envio/*` prefix was reclaimed by the "Envío" AppShell (storefront), so
 * old email links now land inside the shop shell as `/envio/{id}` with no
 * matching relative route → 404.
 *
 * This component is mounted **relatively inside the `envio` AppShell** as
 * `<Route path=":id" element={<LegacyEnvioIdRedirect />} />`, so it runs
 * when the visitor lands on `/envio/{some-uuid}`. It disambiguates by
 * probing the backend:
 *
 *   1. `GET /shipments/{id}` → 200: redirect to `/envio-paquete/{id}`.
 *   2. Else `GET /orders/{id}` → 200: redirect to `/{order.shop}/pedido/{id}`.
 *   3. Else: render a "not found" placeholder.
 *
 * The `?…` search string is always preserved so `?ref=email` links keep
 * working. Also swallows 401/403/404 silently — no toasts, no console noise.
 */
export default function LegacyEnvioIdRedirect() {
  const { id } = useParams();
  const location = useLocation();
  const [outcome, setOutcome] = useState(() =>
    UUID_RE.test(id || "")
      ? { kind: "loading" }
      : { kind: "not-found" }
  );

  useEffect(() => {
    // Phase 35 hotfix — non-UUID `:id` (typo, stale bookmark, or a literal
    // path we forgot to register) never touches the backend.
    if (!UUID_RE.test(id || "")) {
      setOutcome({ kind: "not-found" });
      return;
    }

    let alive = true;

    (async () => {
      // Step 1 — probe shipments (most likely for `/envio/{id}` legacy links).
      try {
        await api.get(`/shipments/${id}`);
        if (alive) {
          setOutcome({
            kind: "redirect",
            to: `/envio-paquete/${id}${location.search || ""}`,
          });
        }
        return;
      } catch (_e) {
        // Fall through to orders probe.
      }

      // Step 2 — probe orders. If the caller owns the order we'll get 200 +
      // `shop` field to route them properly; otherwise fall back to cookie.
      try {
        const { data } = await api.get(`/orders/${id}`);
        const orderShop =
          data?.shop === "envio" || data?.shop === "local"
            ? data.shop
            : DEFAULT_SHOP;
        const prefix = (SHOP_META[orderShop] || SHOP_META[DEFAULT_SHOP]).prefix;
        if (alive) {
          setOutcome({
            kind: "redirect",
            to: `${prefix}/pedido/${id}${location.search || ""}`,
          });
        }
        return;
      } catch (_e) {
        // Neither shipment nor order — genuine 404.
      }

      if (alive) setOutcome({ kind: "not-found" });
    })();

    return () => {
      alive = false;
    };
  }, [id, location.search]);

  if (outcome.kind === "loading") {
    return (
      <div
        data-testid="legacy-envio-redirect-loading"
        className="mx-auto max-w-2xl px-6 py-24 text-center"
      >
        <div className="h-32 rounded-2xl bg-neutral-100 animate-pulse" />
        <p className="mt-4 text-sm text-neutral-500">Cargando tu envío…</p>
      </div>
    );
  }
  if (outcome.kind === "redirect") {
    return <Navigate to={outcome.to} replace />;
  }
  // not-found — reuse the same styling as the app's NotFound page but with
  // shipment-specific copy so the customer knows what they were looking for.
  return (
    <div
      data-testid="legacy-envio-redirect-not-found"
      className="mx-auto max-w-2xl px-6 py-24 text-center"
    >
      <h1 className="text-3xl font-black tracking-tight">Envío no encontrado</h1>
      <p className="mt-3 text-neutral-500">
        Es posible que el enlace no sea válido o que estés en otro dispositivo.
      </p>
    </div>
  );
}
